import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Picker } from 'antd-mobile';
import type { PickerValue } from 'antd-mobile/es/components/picker';
// Vite CJS 预构建兼容：先取 default 再解构，避免 named import 在 Vite dev 环境失效
import LunarJS from 'lunar-javascript';
const { Solar, Lunar, LunarYear, LunarMonth } = LunarJS;

// ========== 类型 ==========
/** 星座（按月+日） */
const getXingzuo = (m: number, d: number): string => {
  const days = [20,19,21,20,21,22,23,23,23,23,22,22];
  const names = ['摩羯座','水瓶座','双鱼座','白羊座','金牛座','双子座','巨蟹座','狮子座','处女座','天秤座','天蝎座','射手座','摩羯座'];
  return d < days[m - 1] ? names[m - 1] : names[m];
};
/** 星期名 */
const WEEKDAYS = ['星期日','星期一','星期二','星期三','星期四','星期五','星期六'];

export interface DateTimeResult {
  solarDate: string;          // "2026-04-24"
  solarDisplay: string;       // "2026年4月24日"
  lunarString: string;        // "乙巳年三月初八"
  lunarMonth: number;         // 农历月份（负数=闰月）
  lunarDay: number;
  hour: number;               // 时辰 0-11
  hourUnknown: boolean;
  inputMode: 'solar' | 'lunar';
  // 老黄历信息
  zodiacAnimal: string;        // 生肖 "蛇"
  yearGanZhi: string;          // 年干支 "乙巳"
  monthGanZhi: string;         // 月干支 "庚辰"
  dayGanZhi: string;           // 日干支
  weekday: number;             // 0=日 1=1 ... 6=六
  xingzuo: string;             // 星座
}

interface DatePickerProProps {
  value: string;           // YYYY-MM-DD
  onChange: (result: DateTimeResult) => void;
  minYear?: number;
  maxYear?: number;
  className?: string;
  disabled?: boolean;
}

// ========== 常量 ==========
const HOUR_NAMES = ['子时','丑时','寅时','卯时','辰时','巳时','午时','未时','申时','酉时','戌时','亥时'];

// ========== 工具函数 ==========
const getSolarDays = (y: number, m: number) => new Date(y, m, 0).getDate();

const getLunarMonthOptions = (year: number) => {
  const ly = LunarYear.fromYear(year);
  const leap = ly.getLeapMonth();
  const names = ['正月','二月','三月','四月','五月','六月','七月','八月','九月','十月','冬月','腊月'];
  const result: { value: number; label: string }[] = [];
  for (let m = 1; m <= 12; m++) {
    result.push({ value: m, label: names[m - 1] });
    if (leap > 0 && m === leap) {
      result.push({ value: -leap, label: `闰${names[leap - 1]}` });
    }
  }
  return result;
};

const getLunarDays = (year: number, month: number): number => {
  try { return LunarMonth.fromYm(year, month).getDayCount(); }
  catch { return 29; }
};

const solarToLunarStr = (y: number, m: number, d: number): string => {
  try {
    const l = Solar.fromYmd(y, m, d).getLunar();
    return `${l.getYearInGanZhi()}年${l.getMonthInChinese()}月${l.getDayInChinese()}`;
  } catch { return ''; }
};

const lunarDisplayStr = (ly: number, lm: number, ld: number): string => {
  try {
    const l = Lunar.fromYmd(ly, Math.abs(lm), ld);
    const isLeap = lm < 0;
    const monthStr = isLeap ? `闰${l.getMonthInChinese()}` : l.getMonthInChinese();
    return `${l.getYearInGanZhi()}年${monthStr}月${l.getDayInChinese()}`;
  } catch { return ''; }
};

const lunarToSolarStr = (y: number, m: number, d: number): string => {
  try {
    const s = Lunar.fromYmd(y, m, d).getSolar();
    return `${s.getYear()}年${s.getMonth()}月${s.getDay()}日`;
  } catch { return ''; }
};

const DAY_CHINESE = [
  '初一','初二','初三','初四','初五','初六','初七','初八','初九','初十',
  '十一','十二','十三','十四','十五','十六','十七','十八','十九','二十',
  '廿一','廿二','廿三','廿四','廿五','廿六','廿七','廿八','廿九','三十',
];

// ========== antd-mobile Picker 列数据类型 ==========
interface PickerColumnItem {
  label: string;
  value: string;
}

type PickerValues = PickerValue[];

// ========== 主组件 ==========
const DatePickerPro: React.FC<DatePickerProProps> = ({
  value, onChange, minYear = 1900, maxYear = new Date().getFullYear(),
  className = '', disabled = false,
}) => {
  const now = new Date();
  const defaultYear = now.getFullYear();

  // ---- 模式 ----
  const [mode, setMode] = useState<'solar' | 'lunar'>('solar');

  // ---- 公历 ----
  const [sYear, setSYear] = useState(defaultYear);
  const [sMonth, setSMonth] = useState(1);
  const [sDay, setSDay] = useState(1);

  // ---- 农历 ----
  const [lYear, setLYear] = useState(defaultYear);
  const [lMonth, setLMonth] = useState(1);
  const [lDay, setLDay] = useState(1);

  // ---- 时辰 ----
  const [hourOn, setHourOn] = useState(false);
  const [hourIdx, setHourIdx] = useState(5);

  // ---- UI ----
  const [open, setOpen] = useState(false);
  const [confirmedMode, setConfirmedMode] = useState<'solar' | 'lunar' | null>(null);

  // ========== 从外部 value 初始化 ==========
  useEffect(() => {
    if (!value) return;
    const parts = value.split('-');
    if (parts.length < 3) return;
    const y = parseInt(parts[0], 10), m = parseInt(parts[1], 10), d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return;
    setSYear(y);
    setSMonth(m);
    setSDay(Math.min(d, getSolarDays(y, m)));
    try {
      const l = Solar.fromYmd(y, m, Math.min(d, getSolarDays(y, m))).getLunar();
      setLYear(l.getYear());
      setLMonth(l.getMonth());
      setLDay(l.getDay());
    } catch (e) { console.error('[DatePickerPro] init 公历→农历转换失败:', e); }
  }, [value]);

  // ========== Picker 列数据 ==========
  // 年列
  const yearCols = useMemo<PickerColumnItem[]>(() =>
    Array.from({ length: maxYear - minYear + 1 }, (_, i) => ({
      label: `${minYear + i}年`,
      value: String(minYear + i),
    })),
    [minYear, maxYear]
  );

  // 公历月列
  const solarMonthCols = useMemo<PickerColumnItem[]>(() =>
    Array.from({ length: 12 }, (_, i) => ({
      label: `${i + 1}月`,
      value: String(i + 1),
    })),
    []
  );

  // 公历日列
  const solarDayCols = useMemo<PickerColumnItem[]>(() =>
    Array.from({ length: getSolarDays(sYear, sMonth) }, (_, i) => ({
      label: `${i + 1}日`,
      value: String(i + 1),
    })),
    [sYear, sMonth]
  );

  // 农历月列
  const lunarMonthOpts = useMemo(() => getLunarMonthOptions(lYear), [lYear]);
  const lunarMonthCols = useMemo<PickerColumnItem[]>(() =>
    lunarMonthOpts.map(o => ({ label: o.label, value: String(o.value) })),
    [lunarMonthOpts]
  );

  // 农历日列
  const lunarDayCols = useMemo<PickerColumnItem[]>(() => {
    const count = getLunarDays(lYear, lMonth);
    return Array.from({ length: count }, (_, i) => ({
      label: i < 30 ? DAY_CHINESE[i] : String(i + 1),
      value: String(i + 1),
    }));
  }, [lYear, lMonth]);

  // 时辰列
  const hourCols = useMemo<PickerColumnItem[]>(() =>
    HOUR_NAMES.map((name, i) => ({ label: name, value: String(i) })),
    []
  );

  // ========== Picker columns + value ==========
  const pickerColumns = useMemo(() => {
    const cols: PickerColumnItem[][] = [];
    if (mode === 'solar') {
      cols.push(yearCols, solarMonthCols, solarDayCols);
    } else {
      cols.push(yearCols, lunarMonthCols, lunarDayCols);
    }
    if (hourOn) cols.push(hourCols);
    return cols;
  }, [mode, yearCols, solarMonthCols, solarDayCols, lunarMonthCols, lunarDayCols, hourOn, hourCols]);

  const pickerValue = useMemo((): PickerValues => {
    if (mode === 'solar') {
      const vals = [String(sYear), String(sMonth), String(sDay)];
      if (hourOn) vals.push(String(hourIdx));
      return vals;
    } else {
      const vals = [String(lYear), String(lMonth), String(lDay)];
      if (hourOn) vals.push(String(hourIdx));
      return vals;
    }
  }, [mode, sYear, sMonth, sDay, lYear, lMonth, lDay, hourOn, hourIdx]);

  // ========== 动态标题 ==========
  const title = useMemo(() => {
    try {
      if (mode === 'solar') {
        return `${sYear}年${sMonth}月${sDay}日 → ${solarToLunarStr(sYear, sMonth, sDay)}`;
      } else {
        const solarStr = lunarToSolarStr(lYear, lMonth, lDay);
        return `${lunarDisplayStr(lYear, lMonth, lDay)} → ${solarStr}`;
      }
    } catch { return ''; }
  }, [mode, sYear, sMonth, sDay, lYear, lMonth, lDay]);

  // ========== 模式切换 ==========
  const switchMode = (m: 'solar' | 'lunar') => {
    if (m === mode) return;
    try {
      if (m === 'lunar') {
        const l = Solar.fromYmd(sYear, sMonth, Math.min(sDay, getSolarDays(sYear, sMonth))).getLunar();
        setLYear(l.getYear());
        setLMonth(l.getMonth());
        setLDay(l.getDay());
      } else {
        const s = Lunar.fromYmd(lYear, lMonth, Math.min(lDay, getLunarDays(lYear, lMonth))).getSolar();
        setSYear(s.getYear());
        setSMonth(s.getMonth());
        setSDay(s.getDay());
      }
    } catch {
      setSYear(defaultYear);
      setSMonth(now.getMonth() + 1);
      setSDay(now.getDate());
    }
    setMode(m);
  };

  // ========== 当前时刻 ==========
  const goNow = () => {
    const y = defaultYear, m = now.getMonth() + 1, d = now.getDate();
    setSYear(y); setSMonth(m); setSDay(d);
    try {
      const l = Solar.fromYmd(y, m, d).getLunar();
      setLYear(l.getYear()); setLMonth(l.getMonth()); setLDay(l.getDay());
    } catch (e) { console.error('[DatePickerPro] goNow 公历→农历转换失败:', e); }
    const h = now.getHours();
    setHourIdx(h >= 23 ? 0 : Math.floor((h + 1) / 2));
    if (!hourOn) setHourOn(true);
    setMode('solar');
  };

  // ========== Picker onChange — 联动更新 ==========
  const handlePickerChange = useCallback((val: PickerValues) => {
    if (mode === 'solar') {
      const y = parseInt(String(val[0]), 10);
      const m = parseInt(String(val[1]), 10);
      const d = parseInt(String(val[2]), 10);
      if (!isNaN(y)) setSYear(y);
      if (!isNaN(m)) {
        setSMonth(m);
        // 联动：限制日不超过该月最大天数
        if (!isNaN(y) && !isNaN(d)) {
          setSDay(Math.min(d, getSolarDays(y, m)));
        }
      }
      if (!isNaN(d)) setSDay(d);
    } else {
      const y = parseInt(String(val[0]), 10);
      const m = parseInt(String(val[1]), 10);
      const d = parseInt(String(val[2]), 10);
      if (!isNaN(y)) setLYear(y);
      if (!isNaN(m)) {
        setLMonth(m);
        if (!isNaN(y) && !isNaN(d)) {
          setLDay(Math.min(d, getLunarDays(y, m)));
        }
      }
      if (!isNaN(d)) setLDay(d);
    }
    if (hourOn && val[3] !== undefined) {
      const h = parseInt(String(val[3]), 10);
      if (!isNaN(h)) setHourIdx(h);
    }
  }, [mode, hourOn]);

  // ========== 确定（接收 Picker 的最终选中值）==========
  const confirm = (val?: PickerValues) => {
    try {
      // 优先使用 onConfirm 传递的值，fallback 到组件 state
      const useVal = val !== undefined && val.length >= 3;
      let sy: number, sm: number, sd: number, ly: number, lm: number, ld: number;
      if (mode === 'solar') {
        if (useVal) {
          sy = parseInt(String(val[0]), 10) || sYear;
          sm = parseInt(String(val[1]), 10) || sMonth;
          sd = Math.min(parseInt(String(val[2]), 10) || sDay, getSolarDays(sy, sm));
        } else {
          sy = sYear; sm = sMonth; sd = Math.min(sDay, getSolarDays(sYear, sMonth));
        }
        const l = Solar.fromYmd(sy, sm, sd).getLunar();
        ly = l.getYear(); lm = l.getMonth(); ld = l.getDay();
      } else {
        if (useVal) {
          ly = parseInt(String(val[0]), 10) || lYear;
          lm = parseInt(String(val[1]), 10) || lMonth;
          ld = Math.min(parseInt(String(val[2]), 10) || lDay, getLunarDays(ly, lm));
        } else {
          ly = lYear; lm = lMonth; ld = Math.min(lDay, getLunarDays(lYear, lMonth));
        }
        const s = Lunar.fromYmd(ly, lm, ld).getSolar();
        sy = s.getYear(); sm = s.getMonth(); sd = s.getDay();
      }
      // 时辰取自 Picker 第4列或 state
      let hour = hourIdx;
      if (hourOn && useVal && val[3] !== undefined) {
        const h = parseInt(String(val[3]), 10);
        if (!isNaN(h)) hour = h;
      }
      const solarDate = `${sy}-${String(sm).padStart(2, '0')}-${String(sd).padStart(2, '0')}`;
      const solarDisplay = `${sy}年${sm}月${sd}日`;
      const solarObj = Solar.fromYmd(sy, sm, sd);
      const lunarObj = solarObj.getLunar();
      const lunarStr = `${lunarObj.getYearInGanZhi()}年${lunarObj.getMonthInChinese()}月${lunarObj.getDayInChinese()}`;

      setConfirmedMode(mode);
      onChange({
        solarDate,
        solarDisplay,
        lunarString: lunarStr,
        lunarMonth: lm, lunarDay: ld,
        hour,
        hourUnknown: !hourOn,
        inputMode: mode,
        zodiacAnimal: lunarObj.getYearShengXiao() || '',
        yearGanZhi: lunarObj.getYearInGanZhi(),
        monthGanZhi: lunarObj.getMonthInGanZhi(),
        dayGanZhi: lunarObj.getDayInGanZhi(),
        weekday: solarObj.getWeek(),
        xingzuo: getXingzuo(sm, sd),
      });
    } catch (e) {
      console.error('[DatePickerPro] confirm 失败:', e);
    }
    setOpen(false);
  };

  // ========== 触发面板信息 ==========
  const hasResult = !!(value && confirmedMode);

  const resultInfo = useMemo(() => {
    if (!value) return null;
    try {
      const parts = value.split('-');
      if (parts.length !== 3) return null;
      const sy = parseInt(parts[0], 10), sm = parseInt(parts[1], 10), sd = parseInt(parts[2], 10);
      if (isNaN(sy) || isNaN(sm) || isNaN(sd)) return null;
      const solarObj = Solar.fromYmd(sy, sm, sd);
      const lunarObj = solarObj.getLunar();
      return {
        solarDisplay: `${sy}年${sm}月${sd}日`,
        lunarFull: `${lunarObj.getYearInGanZhi()}年${lunarObj.getMonthInChinese()}月${lunarObj.getDayInChinese()}`,
        zodiac: lunarObj.getYearShengXiao() || '',
        weekday: WEEKDAYS[solarObj.getWeek()] || '',
        xingzuo: getXingzuo(sm, sd),
        yearGZ: lunarObj.getYearInGanZhi(),
        dayGZ: lunarObj.getDayInGanZhi(),
      };
    } catch { return null; }
  }, [value]);

  // ========== UI 渲染 ==========
  return (
    <div className={`date-picker-pro ${className}`}>
      {/* ---- 信息展示面板（点击打开选择器）---- */}
      <button
        type="button"
        className={`w-full rounded-xl border cursor-pointer transition-all box-border text-left overflow-hidden ${
          disabled ? 'opacity-50 pointer-events-none' : ''
        } ${
          open
            ? 'border-brand-accent/60 shadow-[0_0_20px_rgba(201,162,39,0.12)]'
            : 'border-brand-tertiary/40 hover:border-brand-accent/30'
        }`}
        style={{ backgroundColor: open ? 'rgba(201,162,39,0.08)' : 'rgba(23,23,23,0.6)' }}
        onClick={() => { if (!disabled) setOpen(true); }}
        disabled={disabled}
        aria-label={hasResult ? "点击修改生日日期" : "选择生日日期"}
        aria-expanded={open}
      >
        {hasResult && resultInfo ? (
          /* ========== 已选状态：宽松布局展示丰富信息 ========== */
          <div className="px-4" style={{ paddingTop: '14px', paddingBottom: '18px' }}>
            {/* 第一行：主日期（大字）+ 修改按钮 */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex-1 min-w-0 mr-2">
                {confirmedMode === 'lunar' ? (
                  <div className="font-comic-chinese font-bold text-brand-accent leading-tight" style={{ fontSize: '1.375rem' }}>{resultInfo.lunarFull}</div>
                ) : (
                  <div className="font-serif-chinese font-semibold text-white leading-tight" style={{ fontSize: '1.5rem' }}>{resultInfo.solarDisplay}</div>
                )}
                <div className="text-neutral-500 mt-0.5" style={{ fontSize: '0.875rem' }}>{resultInfo.weekday}</div>
              </div>
              {/* 修改按钮 — 仅图标，紧凑圆形 */}
              <div className="flex items-center justify-center rounded-full bg-neutral-800/60 border border-neutral-700/50 flex-shrink-0" style={{ width: '24px', height: '24px' }}>
                <svg className="w-3 h-3 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                </svg>
              </div>
            </div>

            {/* 第二行：副日期 + 时辰 */}
            <div className="flex items-center flex-wrap gap-x-4 mb-2.5" style={{ fontSize: '0.9375rem', lineHeight: '1.6' }}>
              {confirmedMode === 'lunar' ? (
                <span className="text-neutral-300">公历 {resultInfo.solarDisplay}</span>
              ) : (
                <span className="text-neutral-300">{resultInfo.lunarFull}</span>
              )}
              {hourOn && (
                <span className="text-brand-accent/80">
                  {HOUR_NAMES[hourIdx]}
                  <span className="text-neutral-500 ml-0.5" style={{ fontSize: '0.75rem' }}>
                    ({['23-1','1-3','3-5','5-7','7-9','9-11','11-13','13-15','15-17','17-19','19-21','21-23'][hourIdx]}点)
                  </span>
                </span>
              )}
            </div>

            {/* 第三行：标签组（属相 · 星座 · 干支） */}
            <div className="flex items-center flex-wrap" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', gap: '12px', paddingTop: '10px', paddingBottom: '6px' }}>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full border text-neutral-300"
                style={{ backgroundColor: 'rgba(201,162,39,0.1)', borderColor: 'rgba(201,162,39,0.2)', fontSize: '0.875rem' }}>
                🐲 {resultInfo.zodiac}
              </span>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-neutral-800/60 border border-neutral-700/40 text-neutral-400"
                style={{ fontSize: '0.875rem' }}>
                ⭐ {resultInfo.xingzuo}
              </span>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-neutral-800/60 border border-neutral-700/40 text-neutral-500"
                style={{ fontSize: '0.875rem' }}>
                📅 {resultInfo.yearGZ}年
              </span>
            </div>
          </div>
        ) : (
          /* ========== 未选状态：简洁提示 ========== */
          <div className="flex items-center justify-between px-5 py-5 min-h-[72px]">
            <div>
              <div className="text-lg text-brand-accent font-medium">选择出生日期</div>
              <div className="text-sm text-neutral-500 mt-1">支持公历 / 农历 / 时辰</div>
            </div>
            <div className="w-6 h-6 flex items-center justify-center">
              <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        )}
      </button>

      {/* ---- antd-mobile Picker 底部弹窗 ---- */}
      <Picker
        columns={pickerColumns}
        value={pickerValue}
        onConfirm={confirm}
        onClose={() => setOpen(false)}
        onSelect={handlePickerChange}
        visible={open}
        popupClassName="date-picker-antd-popup"
        confirmText={
          <span style={{ color: '#c9a227', fontWeight: 600 }}>确定</span>
        }
        cancelText="取消"
        title={
          <div style={{ textAlign: 'center', width: '100%' }}>
            {/* 第一行：公历/农历 pill */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '6px' }}>
              <div style={{ display: 'flex', borderRadius: '999px', padding: '2px', backgroundColor: 'rgba(255,255,255,0.08)' }}>
                <button
                  type="button"
                  style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 500, border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                    backgroundColor: mode === 'solar' ? '#c9a227' : 'transparent',
                    color: mode === 'solar' ? '#0a0a0f' : '#737373' }}
                  onClick={(e) => { e.stopPropagation(); switchMode('solar'); }}
                >公历</button>
                <button
                  type="button"
                  style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 500, border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                    backgroundColor: mode === 'lunar' ? '#c9a227' : 'transparent',
                    color: mode === 'lunar' ? '#0a0a0f' : '#737373' }}
                  onClick={(e) => { e.stopPropagation(); switchMode('lunar'); }}
                >农历</button>
              </div>
            </div>
            {/* 第二行：动态日期标题 */}
            <div style={{ fontSize: '11px', color: 'rgba(201,162,39,0.8)', fontWeight: 500, lineHeight: '1.4' }}>
              {title}
            </div>
          </div>
        }
      />
      {/* 底部工具栏 — 嵌入 Picker 弹窗内（当前时刻 + 时辰开关） */}
      {open && (
        <div
          className="date-picker-toolbar"
          style={{
            position: 'fixed',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: '430px',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '10px 16px',
            paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
            backgroundColor: '#141420',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            boxSizing: 'border-box',
          }}
        >
          <button
            type="button"
            style={{ fontSize: '12px', fontWeight: 500, color: 'rgba(201,162,39,0.8)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', transition: 'background 0.2s' }}
            onClick={goNow}
          >⏱ 当前时刻</button>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', padding: '4px 0' }}
            onClick={() => setHourOn(!hourOn)}
          >
            <span style={{ fontSize: '11px', color: '#737373' }}>时辰</span>
            <div
              style={{
                width: '38px', height: '22px', borderRadius: '999px',
                backgroundColor: hourOn ? '#c9a227' : 'rgba(255,255,255,0.1)',
                position: 'relative', transition: 'background-color 0.2s',
              }}
            >
              <div
                style={{
                  width: '18px', height: '18px', borderRadius: '999px',
                  backgroundColor: '#fff', position: 'absolute',
                  top: '2px', left: hourOn ? '17px' : '2px',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePickerPro;
